import { AcceptedRestriction, FixAction, Flag, PATH_SEPARATOR, StudioDraft } from "./studioTypes";
import type { Cdt0Resolution } from "./cdt0Resolution";

export const reorderSpineLevel = (draft: StudioDraft, from: number, to: number): StudioDraft => {
  if (from === to || from < 0 || to < 0 || from >= draft.spine.length || to >= draft.spine.length) {
    return draft;
  }
  const spine = [...draft.spine];
  // Orientation belongs to tier position, not the attribute that lands there.
  const orientByTier = spine.map((level) => level.orient);
  const [moved] = spine.splice(from, 1);
  spine.splice(to, 0, moved);
  const repatterned = spine.map((level, tier) => ({ ...level, orient: orientByTier[tier] }));
  // Path-keyed maps belong to the old hierarchy; dropping them also puts layout in fresh mode.
  return {
    ...draft,
    spine: repatterned,
    scopedValueOrder: {},
    scopedLevelOrder: {},
    bandOverrides: {},
    orientOverrides: {},
    shelfHints: {},
  };
};

export const reorderSiblings = (
  draft: StudioDraft,
  scopeKey: string,
  order: readonly string[],
): StudioDraft => ({
  ...draft,
  scopedValueOrder: { ...draft.scopedValueOrder, [scopeKey]: order },
});

export const moveSibling = (
  draft: StudioDraft,
  scopeKey: string,
  attr: string,
  value: string,
  offset: number,
  /** Offset is in the drawn sibling list so a step lands on the neighbour the user sees. */
  siblings?: readonly string[],
): StudioDraft => {
  const current = [
    ...(siblings ?? draft.scopedValueOrder[scopeKey] ?? draft.valueOrder[attr] ?? []),
  ];
  const from = current.indexOf(value);
  if (from < 0) return draft;
  const to = from + offset;
  if (to < 0 || to >= current.length) return draft;
  current.splice(from, 1);
  current.splice(to, 0, value);
  return reorderSiblings(draft, scopeKey, current);
};

const applyFixAction = (draft: StudioDraft, action: FixAction): StudioDraft => {
  switch (action.type) {
    case "reorderValues":
      return {
        ...draft,
        scopedValueOrder: { ...draft.scopedValueOrder, [action.scopeKey]: action.order },
      };
    case "retagItems": {
      const attrOverrides = { ...draft.attrOverrides };
      for (const skuId of action.skuIds) {
        attrOverrides[skuId] = { ...attrOverrides[skuId], [action.attr]: action.value };
      }
      return { ...draft, attrOverrides };
    }
  }
};

export const applyFix = (draft: StudioDraft, flag: Flag): StudioDraft => {
  if (draft.appliedFixFlagIds.includes(flag.id)) return draft;
  const next = flag.fix ? applyFixAction(draft, flag.fix) : draft;
  return { ...next, appliedFixFlagIds: [...draft.appliedFixFlagIds, flag.id] };
};

/** Always record the exception; emit a restriction row only for a shipped CDT0 — a phantom MAY_HAVE empties the cell for every real variant. */
export const acceptFlag = (draft: StudioDraft, flag: Flag, cdt0?: Cdt0Resolution): StudioDraft => {
  if (isFlagAccepted(draft, flag.id)) return draft;
  const accepted = {
    ...draft,
    acceptedFlagIds: [...(draft.acceptedFlagIds ?? []), flag.id],
  };
  // CDT0 comes from the contract, not the path, so the row matches the assignments CSV.
  const variant = cdt0?.variantFor(flag.skuIds) ?? null;
  if (variant === null) return accepted;
  const kind: AcceptedRestriction["kind"] =
    flag.kind === "split" ? "split-off" : flag.kind === "orphan" ? "breakout" : "shelf-rule";
  const restriction: AcceptedRestriction = {
    id: `restriction:${flag.id}`,
    kind,
    cdtPath: [variant],
    collapsedKey: null,
    // Null is not "*": finalize measures, or the upload drops the row.
    placement: null,
    skuIds: flag.skuIds,
    source: { origin: "accepted-violation", flagId: flag.id },
    text: flag.title,
  };
  return { ...accepted, restrictions: [...accepted.restrictions, restriction] };
};

export const isFlagAccepted = (draft: StudioDraft, flagId: string): boolean =>
  (draft.acceptedFlagIds ?? []).includes(flagId) ||
  draft.restrictions.some((restriction) => restriction.source.flagId === flagId);

export const dismissFlag = (draft: StudioDraft, flagId: string): StudioDraft =>
  draft.dismissedFlagIds.includes(flagId)
    ? draft
    : { ...draft, dismissedFlagIds: [...draft.dismissedFlagIds, flagId] };

const SEVERITY_ORDER = { blocking: 0, toCheck: 1, toReview: 2 } as const;

export const resolveAll = (draft: StudioDraft, flags: readonly Flag[]): StudioDraft => {
  const open = flags
    .filter((flag) => !isFlagSettled(draft, flag.id))
    .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  let next = draft;
  for (const flag of open) {
    next = applyFix(next, flag);
  }
  return next;
};

export const isFlagSettled = (draft: StudioDraft, flagId: string): boolean =>
  draft.appliedFixFlagIds.includes(flagId) ||
  draft.dismissedFlagIds.includes(flagId) ||
  isFlagAccepted(draft, flagId);

export const scopeKeyOf = (path: readonly string[]) => path.join(PATH_SEPARATOR);
